/* eslint-disable max-len */
import { useState } from "react";
import { DateRange } from "@mui/x-date-pickers-pro";
import dayjs from "dayjs";
import {
  FunctionField,
  useListContext,
  useRecordContext,
  useRefresh,
  useTranslate,
  useUpdate
} from "react-admin";
import * as User from "providers/user";
import CustomDataGrid from "./CustomDataGrid";
import { EmptyAppointmentIcon, StarIcon } from "assets/Icons";
import {
  Avatar,
  AvatarGroup,
  Badge,
  Box,
  ListItemButton,
  Theme,
  useMediaQuery
} from "@mui/material";
import { Link } from "react-router-dom";
import { IServiceAddOn } from "types";
import { CustomButton } from "./CustomButton";
import { muiColors } from "themes/colors";
import ListDotsActions from "./ListDotsActions";
import CancellationModal from "./CancellationModal";
import { getZonedDate } from "../utils/helpers";
import CustomEmptyList from "./CustomEmptyList";
import { useNavigate } from "react-router-dom";
import LimitedCustomerPreviewModal from "./LimitedCustomerPreviewModal";

const isFirefox = navigator.userAgent.toLowerCase().includes("firefox");

const CustomAppointmentListContent = ({
  fromArtistDetails = false,
  dateValue
}: {
  dateValue?: DateRange<dayjs.Dayjs>;
  fromArtistDetails?: boolean;
}) => {
  const { user, permissions } = User.useUser();
  const translate = useTranslate();
  const [edit] = useUpdate();
  const refresh = useRefresh();
  const isSmall = useMediaQuery<Theme>("(max-width:800px)");
  const [closeFromParent, setCloseFromParent] = useState<boolean>(false);
  const [cancelModalAppointmentId, setCancelModalAppointmentId] = useState<
    string | null
  >(null);
  const [limitedCustomerPreview, setLimitedCustomerPreview] =
    useState<any>(null);

  const groupByDate = (items: any) => {
    const locationTz = user?.company?.locations?.[0]?.timezone || "PST8PDT";

    return items?.reduce((groups: any, item: any) => {
      const date = dayjs(item?.start_time).tz(locationTz);
      const dateKey = date.format("YYYY-MM-DD");

      if (!groups[dateKey]) {
        const isToday = dayjs().tz(locationTz).isSame(date, "day");

        groups[dateKey] = {
          date: isToday
            ? `Today ${date.format("M-D-YY")}`
            : date.format("dddd M-D-YY"),
          id: item.id
        };
      }
      return groups;
    }, {});
  };

  const { data } = useListContext();

  const groupedData = data && groupByDate(data);
  const itemsWithDateSeparator = groupedData && Object.values(groupedData);

  const handleCheckIn = (id: string) => {
    const updatedAppointment = {
      id,
      data: {
        checked_in_at: new Date().toISOString()
      }
    };

    edit("Appointment", updatedAppointment, {
      onSuccess: () => {
        refresh();
      },
      onError: () => {
        // eslint-disable-next-line no-console
        console.error(`Error occurred during checked_in_at action.`);
      }
    });
  };

  return (
    <div className="overflow-hidden">
      <CustomDataGrid
        rowSx={(item: any) => {
          const isWithDateSeparator = itemsWithDateSeparator?.find(
            (el: any) => el.id === item.id
          );
          if (isWithDateSeparator) {
            return {
              borderTop: "solid 28px #fff",
              position: "sticky",
              "@media screen and (max-width:800px)": {
                borderTop: isFirefox ? "unset" : "solid 24px #fff",
                "& td:first-of-type": {
                  marginTop: isFirefox ? "16px" : null
                }
              },
              "::after": {
                content: `"${isWithDateSeparator?.date}"`,
                position: "absolute",
                top: isFirefox ? null : "-42px",
                left: "0px",
                height: "28px",
                width: "100%",
                backgroundColor: "#f4efe9",
                fontSize: "14px",
                fontWeight: "500",
                fontFamily: "Inter",
                borderBottom: "1px solid rgba(224, 224, 224, 1)",
                borderTop: "1px solid rgba(224, 224, 224, 1)",
                lineHeight: "24px",
                paddingLeft: "16px",
                zIndex: isFirefox ? "4" : null,
                "@media screen and (max-width:800px)": {
                  top: isFirefox ? "-2px" : "-36px",
                  zIndex: 1
                }
              }
            };
          }
        }}
        empty={
          <div
            style={{
              height: "calc(100vh - 200px)",
              backgroundColor: "#F6F4F1"
            }}
          >
            <CustomEmptyList
              emptyIcon={<EmptyAppointmentIcon />}
              emptyTitle={
                dateValue?.[0]
                  ? translate("empty_list.no_appointments_were_found")
                  : translate("empty_list.no_appointments_yet")
              }
              emptyListCreateSubtitle=""
              createButtonText=""
              hasCreate={false}
            />
          </div>
        }
      >
        <FunctionField
          label={translate("appointment.customer")}
          data-label={translate("appointment.customer") + ":"}
          sx={{ marginTop: "12px" }}
          render={(record: any) => {
            const numbers = record.appointment_services?.map(
              (item: any) => item.person_number + 1
            );
            const currentMax = numbers?.length > 0 ? Math.max(...numbers) : 0;
            if (currentMax === 0) return "-";
            return (
              <div className="flex items-center gap-2 -mt-1">
                <div
                  className="flex ml-[12px]"
                  data-label={translate("artists.technicians") + ":"}
                >
                  <AvatarGroup
                    spacing="small"
                    max={currentMax > 2 ? currentMax : 2}
                    sx={{
                      "@media screen and (max-width:1280px)": {
                        flexDirection: "column-reverse"
                      }
                    }}
                  >
                    {[...Array(currentMax)]?.map((item: any, index: number) => (
                      <div key={index}>
                        {index === 0 ? (
                          <Link
                            to={`/dashboard/Customer/${record?.user?.id}/show`}
                            className="flex items-center gap-[16px]"
                          >
                            <Badge
                              overlap="circular"
                              anchorOrigin={{
                                vertical: "bottom",
                                horizontal: "right"
                              }}
                              badgeContent={
                                record?.user?.appointments?.length === 1 ? (
                                  <div
                                    className="w-[12px] h-[12px] flex justify-center items-center
                                    rounded-[6px] bg-mono-primaryLightBgColor"
                                  >
                                    <StarIcon />
                                  </div>
                                ) : (
                                  <div />
                                )
                              }
                            >
                              <Avatar
                                sx={{
                                  bgcolor: "rgba(238, 234, 228, 0.9)",
                                  color: "rgba(48, 47, 46, 0.95)",
                                  width: "32px",
                                  height: "32px",
                                  fontFamily: "Inter",
                                  fontSize: "18px",
                                  "@media screen and (max-width:1280px)": {
                                    width: "24px",
                                    height: "24px",
                                    marginLeft: "0px !important",
                                    fontSize: "16px"
                                  }
                                }}
                                alt={
                                  record?.first_name || record?.user?.first_name
                                }
                                src={
                                  record?.photo_url ||
                                  record?.user?.photo_url ||
                                  "monogram"
                                }
                              />
                            </Badge>
                          </Link>
                        ) : (
                          <Avatar
                            sx={{
                              width: "32px",
                              height: "32px",
                              "@media screen and (max-width:1280px)": {
                                width: "24px",
                                height: "24px",
                                marginLeft: "0px",
                                marginTop: "-10px"
                              }
                            }}
                          />
                        )}
                      </div>
                    ))}
                  </AvatarGroup>
                </div>
                <div>
                  {[...Array(currentMax)]?.map((item: any, index: number) => (
                    <Box
                      key={index}
                      className="line-clamp-1 break-all min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
                    >
                      {index === 0 ? (
                        <>
                          {!permissions?.isCompanyLimited &&
                          !permissions.isCompanyBasic ? (
                            <Link
                              to={`/dashboard/Customer/${record?.user?.id}/show`}
                              onClick={(e) => e.stopPropagation()}
                              style={{ lineHeight: "14px" }}
                            >
                              <span className="text-[#16667A]">
                                {`${record?.user?.first_name} ${record?.user?.last_name}`}
                                {currentMax > 1 && (
                                  <span className="text-[rgba(0,0,0,0.87)]">
                                    {" "}
                                    &
                                  </span>
                                )}
                              </span>
                            </Link>
                          ) : (
                            <div
                              onClick={(e) => {
                                e.stopPropagation();
                                setLimitedCustomerPreview(record?.user);
                              }}
                              aria-hidden
                              className="min-h-[28px] flex items-center"
                            >
                              <span className="text-[#16667A]">
                                {`${record?.user?.first_name} ${record?.user?.last_name}`}
                                {currentMax > 1 && (
                                  <span className="text-[rgba(0,0,0,0.87)]">
                                    {" "}
                                    &
                                  </span>
                                )}
                              </span>
                            </div>
                          )}
                        </>
                      ) : (
                        <span className="whitespace-nowrap">{`Person ${
                          index + 1
                        } ${index === 1 && currentMax > 2 ? "&" : ""}`}</span>
                      )}
                    </Box>
                  ))}
                </div>
              </div>
            );
          }}
        />
        <FunctionField
          label={translate("appointment.location")}
          data-label={translate("appointment.location") + ":"}
          sortable={false}
          render={(record: any) => (
            <div
              className="line-clamp-2 break-all min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
              data-cy="appointment-row"
            >
              {record?.location?.nickname || record?.location?.name}
            </div>
          )}
        />
        <FunctionField
          label={translate("appointment.service")}
          data-label={translate("appointment.service") + ":"}
          render={(record: any) => {
            if (record?.appointment_services?.length === 0) return "-";
            return (
              <div>
                <div className="">
                  {record?.appointment_services?.map(
                    (appointment_service: any, i: number) => (
                      <div
                        key={i}
                        className="line-clamp-1 break-all min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
                      >
                        <span>{appointment_service?.service?.name}</span>
                        {!!appointment_service?.add_ons.length && (
                          <span className="">
                            {" "}
                            (
                            {appointment_service?.add_ons?.map(
                              (addOn: IServiceAddOn, index: number) => (
                                <span key={index}>
                                  {addOn?.core_service?.name}
                                  {index <
                                  appointment_service?.add_ons?.length - 1
                                    ? ", "
                                    : ""}
                                </span>
                              )
                            )}
                            )
                          </span>
                        )}
                        {i < record?.appointment_services?.length - 1 && (
                          <>
                            {" "}
                            &<br />
                          </>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          }}
        />
        <FunctionField
          label={translate("appointment.artists")}
          data-label={translate("appointment.artists") + ":"}
          render={(record: any) => {
            if (record?.appointment_services?.length === 0) return "-";
            return (
              <div>
                <div>
                  {record?.appointment_services?.map(
                    (appointment_service: any, i: number) => (
                      <div
                        key={i}
                        className="line-clamp-1 break-all min-w-[100px] md:min-w-[70px] xl:min-w-[120px]"
                      >
                        {appointment_service?.artist?.user?.first_name}{" "}
                        {appointment_service?.artist?.user?.last_name}
                        {i < record?.appointment_services?.length - 1 && (
                          <>
                            {" "}
                            &<br />
                          </>
                        )}
                      </div>
                    )
                  )}
                </div>
              </div>
            );
          }}
        />
        <FunctionField
          label={translate("appointment.start_time")}
          data-label={translate("appointment.start_time") + ":"}
          source="start_time"
          textAlign="right"
          align="right"
          render={(record: any) => (
            <div>
              <div
                style={{
                  height: isSmall
                    ? ""
                    : record?.appointment_services.length > 1
                    ? "48px"
                    : "",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: isSmall ? "flex-start" : "center"
                }}
              >
                {dayjs(
                  getZonedDate(record?.start_time, record?.location?.timezone)
                ).format("M/D/YYYY, hh:mmA")}
              </div>
            </div>
          )}
        />
        <FunctionField
          label={translate("appointment.end_time")}
          data-label={translate("appointment.end_time") + ":"}
          textAlign="right"
          source="end_time"
          render={(record: any) => (
            <div>
              <div
                style={{
                  height: isSmall
                    ? ""
                    : record?.appointment_services.length > 1
                    ? "48px"
                    : "",
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: isSmall ? "flex-start" : "center"
                }}
              >
                {dayjs(
                  getZonedDate(record?.end_time, record?.location?.timezone)
                ).format("M/D/YYYY, hh:mmA")}
              </div>
            </div>
          )}
        />
        {!isSmall && !fromArtistDetails && (
          <CheckInButton handleCheckIn={handleCheckIn} />
        )}
        {!isSmall && !permissions.isArtist && (
          <FunctionField
            render={(record: any) => (
              <ListDotsActions
                closeFromParent={closeFromParent}
                setCloseFromParent={setCloseFromParent}
              >
                {!(
                  record?.checked_in_at !== null &&
                  record?.created_at !== record?.checked_in_at
                ) &&
                  !record?.cancelled_at && (
                    <ListItemButton
                      onClick={(e) => {
                        handleCheckIn(record?.id);
                        setCloseFromParent(true);
                      }}
                      sx={{
                        fontSize: "14px",
                        fontFamily: "Inter",
                        "& .MuiListItemButton-root": {
                          fontSize: "14px",
                          fontFamily: "Inter"
                        }
                      }}
                    >
                      {translate("appointment.check_in")}
                    </ListItemButton>
                  )}
                {!permissions.isCompanyLimited &&
                  record?.Order &&
                  !record?.cancelled_at && (
                    <Link
                      to={`/dashboard/Appointment-order/${record?.Order?.id}/show`}
                    >
                      <ListItemButton
                        sx={{
                          fontSize: "14px",
                          fontFamily: "Inter",
                          "& .MuiListItemButton-root": {
                            fontSize: "14px",
                            fontFamily: "Inter"
                          }
                        }}
                      >
                        {translate("appointment.charge")}
                      </ListItemButton>
                    </Link>
                  )}
                <Link to={`/dashboard/Appointment/${record?.id}/show`}>
                  <ListItemButton
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Inter",
                      "& .MuiListItemButton-root": {
                        fontSize: "14px",
                        fontFamily: "Inter"
                      }
                    }}
                  >
                    {translate("common.details")}
                  </ListItemButton>
                </Link>
                {record?.Order && !record?.cancelled_at && (
                  <ListItemButton
                    onClick={() => {
                      setCloseFromParent(true);
                      setTimeout(() => {
                        setCancelModalAppointmentId(record?.id || null);
                      }, 500);
                    }}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "Inter",
                      color: muiColors.error,
                      "& .MuiListItemButton-root": {
                        fontSize: "14px",
                        fontFamily: "Inter"
                      }
                    }}
                  >
                    {translate("appointment.cancel")}
                  </ListItemButton>
                )}
              </ListDotsActions>
            )}
          />
        )}
        {isSmall && (
          <FunctionField
            render={(record: any) => (
              <div className="w-full flex gap-2 mt-[6px] mb-[14px]">
                <Link
                  to={`/dashboard/Appointment/${record?.id}/show`}
                  style={{ width: "100%" }}
                >
                  <CustomButton
                    variant="secondary"
                    text={translate("common.view_details")}
                    cyId="view-details-button"
                    sx={{ width: "100%" }}
                  />
                </Link>
                {!fromArtistDetails && (
                  <CheckInButton handleCheckIn={handleCheckIn} mobileView />
                )}
              </div>
            )}
          />
        )}
      </CustomDataGrid>
      {cancelModalAppointmentId && (
        <CancellationModal
          cancelModalAppointmentId={cancelModalAppointmentId}
          setCancelModalAppointmentId={setCancelModalAppointmentId}
        />
      )}
      {limitedCustomerPreview?.id && (
        <LimitedCustomerPreviewModal
          user={limitedCustomerPreview}
          onClose={() => setLimitedCustomerPreview(null)}
        />
      )}
    </div>
  );
};

export default CustomAppointmentListContent;

const CheckInButton = ({
  handleCheckIn,
  mobileView = false
}: {
  handleCheckIn: (id: string) => void;
  mobileView?: boolean;
}) => {
  const translate = useTranslate();
  const { permissions } = User.useUser();
  const navigate = useNavigate();
  const record = useRecordContext();
  const isCancelled = record?.cancelled_at !== null;
  if (isCancelled) {
    return <div className="min-w-[0px] w-full h-[0px]" />;
  }
  return (
    <FunctionField
      label={translate("appointment.today")}
      textAlign="right"
      render={(record: any) => {
        const isCheckedIn =
          record?.checked_in_at !== null &&
          record?.created_at !== record?.checked_in_at;
        const needsCheckOut =
          !!isCheckedIn && record?.Order?.amount_remaining > 0;
        const isCheckedOut = !!isCheckedIn && !needsCheckOut;
        const isCompanyLimited = permissions?.isCompanyLimited;
        return (
          <Box
            className={mobileView ? "" : "w-full flex justify-end"}
            sx={{
              minWidth: "108px",
              "@media screen and (max-width:1280px)": {
                minWidth: "62px"
              }
            }}
          >
            <CustomButton
              text={
                isCheckedIn
                  ? isCompanyLimited
                    ? translate("appointment.checked_in")
                    : isCheckedOut
                    ? translate("appointment.checked_out")
                    : translate("appointment.check_out")
                  : translate("appointment.check_in")
              }
              onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                e.stopPropagation();
                if (isCheckedOut) return;
                if (!isCompanyLimited && needsCheckOut) {
                  navigate(
                    `/dashboard/Appointment-order/${record?.Order?.id}/show`
                  );
                  return;
                }
                handleCheckIn(record?.id);
              }}
              variant={
                (isCompanyLimited && isCheckedIn) || isCheckedOut
                  ? "secondary"
                  : "primary"
              }
              disableRipple={(isCompanyLimited && isCheckedIn) || isCheckedOut}
              sx={{
                height: mobileView ? "36px" : "30px",
                cursor:
                  (isCompanyLimited && isCheckedIn) || isCheckedOut
                    ? "default"
                    : "pointer",
                fontSize: "14px",
                padding: "0px 16px",
                backgroundColor: isCheckedIn
                  ? isCompanyLimited
                    ? muiColors.button.paper
                    : isCheckedOut
                    ? muiColors.button.paper
                    : muiColors.primary
                  : muiColors.button.primaryDark,
                "&:hover": {
                  backgroundColor: isCheckedIn
                    ? isCompanyLimited
                      ? muiColors.button.paper
                      : isCheckedOut
                      ? muiColors.button.paper
                      : muiColors.primary
                    : muiColors.button.primaryDark
                },
                "@media screen and (max-width:1280px)": {
                  fontSize: "12px",
                  padding: "0px 8px"
                }
              }}
            />
          </Box>
        );
      }}
    />
  );
};
